/**
 * Print a PDF in the current page. No extra browser tab.
 * The frame is kept off-screen so only the print dialog is shown.
 * Phone and iPad use the device print dialog instead of this frame.
 */
import { isHandheldDevice, printPdfOnHandheld, saveFileOnHandheld } from './handheldFile.js';

function toPdfBlob(blobData) {
  if (blobData instanceof Blob) {
    if (!blobData.type || blobData.type.includes('pdf') || blobData.type === 'application/octet-stream') {
      return blobData.type.includes('pdf')
        ? blobData
        : new Blob([blobData], { type: 'application/pdf' })
    }
    return blobData
  }
  return new Blob([blobData], { type: 'application/pdf' })
}

async function assertPdfBlob(blob) {
  if (blob.type && !blob.type.includes('pdf') && blob.type !== 'application/octet-stream') {
    const text = await blob.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(parsed.error || parsed.message || text)
    } catch (err) {
      if (err instanceof Error && err.message !== text) throw err
      throw new Error(text || 'Invalid PDF response')
    }
  }
}

const PRINT_FRAME_STYLE =
  'position:fixed;left:-10000px;top:0;width:1200px;height:1600px;border:0;opacity:1'

function mountPrintFrame(title) {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('title', title || 'Print')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = PRINT_FRAME_STYLE
  document.body.appendChild(iframe)
  return iframe
}

function releaseFrame(iframe, url) {
  try {
    iframe.remove()
  } catch {
    /* ignore */
  }
  if (url) {
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

function printFrameWindow(iframe) {
  return new Promise((resolve) => {
    let started = false
    const trigger = () => {
      if (started) return
      started = true
      try {
        const frameWin = iframe.contentWindow
        if (!frameWin) {
          resolve(false)
          return
        }
        frameWin.addEventListener('afterprint', () => {
          setTimeout(() => releaseFrame(iframe), 300)
        })
        frameWin.focus()
        frameWin.print()
        resolve(true)
      } catch {
        resolve(false)
      }
    }

    iframe.addEventListener('load', () => setTimeout(trigger, 400))
    setTimeout(trigger, 1200)
  })
}

function printPdfViaIframe(url, filename) {
  const iframe = mountPrintFrame(filename)
  iframe.src = url
  return printFrameWindow(iframe).then((ok) => {
    if (!ok) releaseFrame(iframe)
    else setTimeout(() => releaseFrame(iframe), 120_000)
    return ok
  })
}

export async function printPdfBlob(
  blobData,
  filename = 'document.pdf',
  { allowDownloadFallback = false } = {},
) {
  const blob = toPdfBlob(blobData)
  await assertPdfBlob(blob)
  const pdfBlob = blob.type.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' })
  if (isHandheldDevice()) {
    const opened = await printPdfOnHandheld(pdfBlob, filename)
    return { method: opened ? 'print' : 'blocked' }
  }
  const url = URL.createObjectURL(pdfBlob)

  const printed = await printPdfViaIframe(url, filename)
  if (printed) return { method: 'print' }

  if (allowDownloadFallback) {
    downloadPdfBlob(pdfBlob, filename)
    URL.revokeObjectURL(url)
    return { method: 'download' }
  }

  URL.revokeObjectURL(url)
  return { method: 'blocked' }
}

export function downloadPdfBlob(blobData, filename = 'document.pdf') {
  const blob = toPdfBlob(blobData)
  const pdfBlob = blob.type && blob.type.includes('pdf')
    ? blob
    : new Blob([blob], { type: 'application/pdf' })
  if (isHandheldDevice()) {
    return saveFileOnHandheld(pdfBlob, filename)
  }
  const url = URL.createObjectURL(pdfBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.target = '_self'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export async function parseBlobErrorMessage(error, fallback = 'Request failed') {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const text = await data.text()
      const parsed = JSON.parse(text)
      return parsed.error || parsed.message || fallback
    } catch {
      return fallback
    }
  }
  return error?.response?.data?.error || error?.message || fallback
}
